/* eslint-disable no-underscore-dangle */
import noop from 'lodash/noop';
import PlayerCache from './PlayerCache';
import LiveSeekPlayer from './wrappers/LiveSeekPlayer';
import InstreamAdPlayer from './wrappers/InstreamAdPlayer';

import players from './constants/players';
import mediaFormats from './constants/mediaFormats';

export default class PlayerLoader extends PlayerCache {
  constructor(configuration) {
    super(configuration);
    this.configuration = configuration;
  }

  /**
   * Public method that returns an array of player instances for the
   * given stream.
   *
   * @param stream {Object}
   * @returns {Promise<any[]>}
   */
  getPlayers(stream) {
    const playerPromises = [
      this._getInstreamAdPlayer(stream),
      this._getLiveSeekPlayer(stream),
      // Must try HLS.js player first otherwise Safari will always choose native implementation.
      this._getHlsJsPlayer(stream),
      this._getWebMediaPlayer(),
      this._getJPlayer(),
      this._getWindowsMediaPlayer(),

      // Don't want player creation errors aborting creation of ALL players.
    ].map((promise) => promise && promise.catch(noop));

    return Promise.all(playerPromises).then((loadedPlayers) =>
      loadedPlayers.filter(Boolean),
    );
  }

  async _getInstreamAdPlayer(stream) {
    if (!stream.isHlsAdvanced) {
      return null;
    }
    const hlsjsPlayer = await this._getPlayerFromCache(players.hlsjs);
    if (!hlsjsPlayer) {
      return null;
    }
    const { timeout, isInstreamLoggingEnabled, tuner } = this.configuration;
    return new InstreamAdPlayer(
      hlsjsPlayer,
      timeout,
      isInstreamLoggingEnabled,
      tuner,
    );
  }

  async _getLiveSeekPlayer(stream) {
    if (!stream.isLiveSeekStream) {
      return null;
    }

    const hlsjsPlayer = await this._getPlayerFromCache(players.hlsjs);

    if (!hlsjsPlayer) {
      return null;
    }

    if (this.configuration.isWebMediaPlayerEnabled) {
      hlsjsPlayer.isLiveSeekStream = true;

      return hlsjsPlayer;
    }

    return new LiveSeekPlayer(hlsjsPlayer);
  }

  _getWebMediaPlayer() {
    if (this.configuration.isWebMediaPlayerEnabled) {
      return this._getPlayerFromCache(players.webmediaplayer);
    }
  }

  _getJPlayer() {
    return this._getPlayerFromCache(players.jplayer);
  }

  _getHlsJsPlayer(stream) {
    if (
      stream.format === mediaFormats.hls &&
      !this.configuration.hasNativeHlsSupport
    ) {
      return this._getPlayerFromCache(players.hlsjs);
    }
  }

  _getWindowsMediaPlayer() {
    return this._getPlayerFromCache(players.wmp);
  }

  async _getPlayerFromCache(playerName) {
    const player = await this.getPlayer(playerName);
    this.setPlayer(playerName);
    return player;
  }
}
